// frontend/src/components/ProjectPickerDialog.js

import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Typography,
  Box,
  Divider
} from '@mui/material';
import { 
  Add as AddIcon, 
  Folder as ProjectIcon,
  Search as SearchIcon 
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const ProjectPickerDialog = ({ 
  open, 
  onClose, 
  onSelect, 
  allowCreate = true, 
  title = "Add to Project",
  excludeProjectId = null 
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const { currentUser } = useAuth();

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce(async (query) => {
      if (!currentUser) return;
      
      setLoading(true);
      try {
        const token = await currentUser.getIdToken();
        // ✅ FIX: Use V2 API to get real Project documents, not fake session-based projects
        const response = await fetch(`/api/v2/projects?search=${encodeURIComponent(query)}&limit=20`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setProjects(data.projects || []);
          if (process.env.NODE_ENV !== 'production') {
            console.log('[ProjectPicker] Search results:', data.projects?.length || 0, 'projects');
          }
        } else {
          console.error('Failed to search projects:', response.statusText);
          setProjects([]);
        }
      } catch (error) {
        console.error('Error searching projects:', error);
        setProjects([]);
      } finally {
        setLoading(false);
      }
    }, 250),
    [currentUser]
  );

  // Load recent projects function - defined before useEffect to avoid dependency issues
  const loadRecentProjects = useCallback(async () => {
    if (!currentUser) return;
    
    setLoading(true);
    try {
      const token = await currentUser.getIdToken();
      // ✅ FIX: Use V2 API to get real Project documents, not fake session-based projects
      const response = await fetch('/api/v2/projects?limit=10&sort=-updatedAt', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setProjects(data.projects || []);
      }
    } catch (error) {
      console.error('Error loading recent projects:', error);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  // Load projects when dialog opens or search changes
  useEffect(() => {
    if (open) {
      if (searchQuery.trim().length >= 2) {
        debouncedSearch(searchQuery.trim());
      } else {
        // Load recent projects when no search query or short query
        loadRecentProjects();
      }
    }
  }, [open, searchQuery, debouncedSearch, loadRecentProjects]);

  const handleCreateProject = async (name) => {
    if (!currentUser || creating) return;
    
    let projectName = name;
    
    // If name is generic "New Project", prompt for actual name
    if (!projectName || projectName === 'New Project') {
      projectName = prompt('Enter project name:');
      if (!projectName?.trim()) return;
      projectName = projectName.trim();
    }
    
    setCreating(true);
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch('/api/projects', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ name: projectName })
      });
      
      if (response.ok) {
        const newProject = await response.json();
        if (process.env.NODE_ENV !== 'production') {
          console.log('[ProjectPicker] Created project:', newProject.name);
        }
        // Mark as new so parent component knows this was just created
        onSelect({ ...newProject, isNew: true });
        handleClose();
      } else {
        console.error('Failed to create project:', response.statusText);
      }
    } catch (error) {
      console.error('Error creating project:', error);
    } finally {
      setCreating(false);
    }
  };

  const handleClose = () => {
    setSearchQuery('');
    setProjects([]);
    setSelectedIndex(0);
    onClose();
  };

  const handleKeyDown = (event) => {
    const items = getSelectableItems();
    
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setSelectedIndex(prev => (prev + 1) % items.length);
        break;
      case 'ArrowUp':
        event.preventDefault();
        setSelectedIndex(prev => (prev - 1 + items.length) % items.length);
        break;
      case 'Enter':
        event.preventDefault();
        const selectedItem = items[selectedIndex];
        if (selectedItem) {
          if (selectedItem.type === 'create') {
            handleCreateProject(selectedItem.name);
          } else {
            onSelect(selectedItem);
            handleClose();
          }
        }
        break;
      case 'Escape':
        handleClose();
        break;
    }
  };

  const getSelectableItems = () => {
    const items = [];
    
    if (allowCreate) {
      // Always show "Create New Project" at the top
      items.push({
        type: 'create',
        name: 'New Project',
        displayName: '+ Create New Project',
        isDefault: true
      });
      
      // If user has typed something and it doesn't match exactly, also show "Create {query}"
      if (searchQuery.trim().length > 0) {
        const exactMatch = projects.find(p => 
          p.name.toLowerCase() === searchQuery.trim().toLowerCase()
        );
        
        if (!exactMatch) {
          items.push({
            type: 'create',
            name: searchQuery.trim(),
            displayName: `Create "${searchQuery.trim()}"`,
            isDefault: false
          });
        }
      }
    }
    
    // Add existing projects after create options, excluding current project
    const filteredProjects = projects.filter(project => 
      excludeProjectId ? (project._id || project.id) !== excludeProjectId : true
    );
    items.push(...filteredProjects);
    
    return items;
  };

  const selectableItems = getSelectableItems();

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        onKeyDown: handleKeyDown
      }}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          variant="outlined"
          placeholder="Search projects..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
          }}
          sx={{ mb: 2 }}
        />
        
        <Box sx={{ minHeight: 300, maxHeight: 400 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
              <CircularProgress size={24} />
            </Box>
          ) : selectableItems.length > 0 ? (
            <List dense>
              {selectableItems.map((item, index) => {
                // Show divider after the last create option, before projects
                const showDivider = item.type !== 'create' && 
                                   index > 0 && 
                                   selectableItems[index - 1].type === 'create';
                
                return (
                  <React.Fragment key={item._id || `create-${item.name}-${item.isDefault}`}>
                    {showDivider && <Divider sx={{ my: 1 }} />}
                    <ListItem disablePadding>
                      <ListItemButton
                        selected={index === selectedIndex}
                        onClick={() => {
                          if (item.type === 'create') {
                            handleCreateProject(item.name);
                          } else {
                            onSelect(item);
                            handleClose();
                          }
                        }}
                        disabled={creating && item.type === 'create'}
                      >
                        <ListItemIcon>
                          {item.type === 'create' ? (
                            creating ? <CircularProgress size={20} /> : <AddIcon />
                          ) : (
                            <ProjectIcon />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary={item.displayName || item.name}
                          secondary={item.type === 'create' ? null : `${item.bookmarkCount || 0} items`}
                        />
                      </ListItemButton>
                    </ListItem>
                  </React.Fragment>
                );
              })}
            </List>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" color="text.secondary">
                {searchQuery.trim().length >= 2 
                  ? `No projects found for "${searchQuery}"`
                  : 'Start typing to search projects'}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

// Utility function for debouncing
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

export default ProjectPickerDialog;